import { useState } from 'react';
import { Info, Clock, EyeIcon, Play, Trash2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { SetupTranscriptDrawer } from '@/components/agent/drawers/SetupTranscriptDrawer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useUnfinishedSetups } from '@/hooks/agent/useUnfinishedSetups';
import { cn } from '@/lib/utils';

interface UnfinishedSetupsProps {
  onResumeSetup: (conversationId: string) => void | Promise<void>;
}

const getTimeAgo = (dateString: string): string => {
  const date = new Date(dateString + 'Z');
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInHours < 1) {
    return "Less than an hour ago";
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
  } else if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
  } else {
    return date.toLocaleDateString();
  }
};

export const UnfinishedSetups = ({ onResumeSetup }: UnfinishedSetupsProps) => {
  const [resumingId, setResumingId] = useState<string | null>(null);
  const [transcriptDrawerOpen, setTranscriptDrawerOpen] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState<any[] | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { unfinishedSetups, isLoading, markAsAbandoned } = useUnfinishedSetups();

  // Brand colors - using Spark Blue and Magenta Beam for gradient
  const brand = '#4E9CFF';
  const brandSecondary = '#FF4DB8';

  const handleResumeSetup = async (conversationId: string) => {
    try {
      setResumingId(conversationId);
      await onResumeSetup(conversationId);
    } catch (error) {
      console.error("Failed to resume setup:", error);
      const errorMessage = error instanceof Error ? error.message : String(error);

      if (errorMessage.includes("transcript") || errorMessage.includes("conversation")) {
        toast.error("We could not bring that setup back", {
          description:
            "This one was started before we began saving setups as you go, so there is nothing left to restore. A new setup is saved automatically.",
        });
      } else {
        toast.error("Failed to resume setup. Please try again.");
      }
    } finally {
      setResumingId(null);
    }
  };

  const handleRemoveSetup = async (conversationId: string) => {
    try {
      markAsAbandoned(conversationId);
      toast.success("Setup removed from list");
    } catch (error) {
      console.error("Failed to remove setup:", error);
      toast.error("Failed to remove setup");
    }
  };

  const handleSeeDetails = (setup: any) => {
    const conversationDetail = Array.isArray(setup.conversation_details)
      ? setup.conversation_details[0]
      : setup.conversation_details;
    setSelectedTranscript(conversationDetail?.transcript || null);
    setTranscriptDrawerOpen(true);
  };

  const handleCloseTranscriptDrawer = () => {
    setTranscriptDrawerOpen(false);
    setSelectedTranscript(null);
  };

  if (isLoading || unfinishedSetups.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Info className="w-5 h-5" style={{ color: brand }} />
        <h2 className="text-lg font-semibold text-gray-900">Unfinished Genie Setups</h2>
        <Badge
          className="ml-auto font-semibold"
          style={{
            backgroundColor: `${brand}15`,
            color: brand,
            borderColor: `${brand}40`,
            borderWidth: '1px',
            borderStyle: 'solid'
          }}
        >
          {unfinishedSetups.length}
        </Badge>
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1 hover:bg-gray-100 rounded-md transition-colors"
          aria-label={isCollapsed ? "Expand unfinished setups" : "Collapse unfinished setups"}
        >
          <ChevronDown
            className="w-5 h-5 text-gray-600 transition-transform duration-200"
            style={{ transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
          />
        </button>
      </div>

      {/* Main Card - Brand-styled like genie cards */}
      <Card
        className="rounded-2xl shadow-lg border-2 overflow-hidden transition-all duration-200"
        style={{
          borderColor: brand,
          boxShadow: `0 10px 15px -3px ${brand}20, 0 4px 6px -2px ${brand}10`
        }}
      >
        {!isCollapsed && (
          <div className="p-4 md:p-6">
            <p className="text-sm text-gray-600 mb-4 md:mb-6">
              You have unfinished genie setups. Resume where you left off or remove them from this list.
            </p>

            <div className="space-y-3 md:space-y-4">
              {unfinishedSetups.map((setup) => (
                <div
                  key={setup.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 md:p-5 bg-white rounded-xl border-2 shadow-sm transition-all hover:shadow-md gap-3"
                  style={{
                    borderColor: `${brand}30`,
                    boxShadow: `0 2px 4px ${brand}10`
                  }}
                >
                  {/* Left side - Status indicator and time */}
                  <div className="flex items-center gap-3">
                    {/* Animated pulse indicator */}
                    <div className="relative flex-shrink-0">
                      <div
                        className="w-3 h-3 rounded-full animate-pulse"
                        style={{ backgroundColor: brand }}
                      />
                      <div
                        className="absolute inset-0 w-3 h-3 rounded-full animate-ping opacity-20"
                        style={{ backgroundColor: brand }}
                      />
                    </div>

                    <div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" style={{ color: `${brand}80` }} />
                        <span className="text-sm font-medium text-gray-700">
                          Setup started {getTimeAgo(setup.created_at)}
                        </span>
                      </div>
                      {/* Say plainly whether this one WILL come back. Recovery
                          used to promise the same thing for every row and then
                          fail for the ones whose conversation data had gone. */}
                      <p
                        className="text-xs mt-0.5"
                        style={{ color: setup.hasPersistedState ? brand : "#64748B" }}
                      >
                        {setup.hasPersistedState
                          ? "Saved and ready to pick up"
                          : "Started before setups were saved as you go, so this one is a best effort"}
                      </p>
                    </div>
                  </div>

                  {/* Right side - Action buttons */}
                  <div className="flex items-center gap-2 md:gap-3 flex-wrap sm:flex-nowrap justify-end sm:justify-start">
                    {/* See details button */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSeeDetails(setup)}
                      className="border-2 font-medium shadow-sm hover:shadow-md transition-all"
                      style={{
                        borderColor: `${brand}40`,
                        color: brand
                      }}
                    >
                      <EyeIcon className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">See details</span>
                    </Button>

                    {/* Resume button - Primary action */}
                    <Button
                      size="sm"
                      onClick={() => handleResumeSetup(setup.elevenlabs_id)}
                      disabled={resumingId === setup.elevenlabs_id}
                      className="font-semibold text-white shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                      style={{
                        backgroundColor: brand
                      }}
                    >
                      {resumingId === setup.elevenlabs_id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent sm:mr-2" />
                          <span className="hidden sm:inline">Resuming...</span>
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 sm:mr-2" />
                          <span className="hidden sm:inline">Resume</span>
                        </>
                      )}
                    </Button>

                    {/* Delete button - gradient themed */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRemoveSetup(setup.elevenlabs_id)}
                      className="border-2 flex-shrink-0 transition-all font-medium shadow-sm hover:shadow-md"
                      style={{
                        borderColor: `${brandSecondary}40`,
                        color: brandSecondary
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <SetupTranscriptDrawer isOpen={transcriptDrawerOpen} onClose={handleCloseTranscriptDrawer} transcript={selectedTranscript} />
    </div>
  );
};
